import React, { useMemo, useState, useEffect } from "react";
import { Table, Pagination } from "antd";
import dayjs from "dayjs";
import { useStatusTrackerV2 } from "../hooks/useStatusTrackerV2";
import {
    selectCurrentCells,
    buildFullRows,
    filterCellsByPlayerIds,
    computeStatusCounts,
} from "../state/selectors";
import { getMonthDates } from "../state/utils";
import { StatusLegend } from "../views/StatusLegend";
import { AddFlagButton } from "./AddFlagButton";
import { DateCell } from "./DateCell";
import { PlayerColumnCell } from "./PlayerColumnCell";
import { PlayerColumnHeader } from "./PlayerColumnHeader";

const PAGE_SIZE = 50;

// Date column header — MMM / DD stacked (matches v1 formattedColumnHeader).
const columnHeader = (dt) => (
    <div style={{ textAlign: "center", padding: "10px" }}>
        <span style={{ fontSize: 20 }}> {dayjs(dt).format("MMM")} </span>
        <span style={{ fontSize: 30, lineHeight: "30px" }}> {dayjs(dt).format("DD")} </span>
    </div>
);

export const StatusTrackerGrid = ({ legendLeading = null, flagTooltipPlacement }) => {
    const { state, team } = useStatusTrackerV2();
    const [page, setPage] = useState(1);

    const { selectedMonth } = state.filters;
    const dateStrs = useMemo(() => getMonthDates(selectedMonth), [selectedMonth]);
    const cells = selectCurrentCells(state);

    const fullRows = useMemo(
        () => buildFullRows(team.players, dateStrs, state.filters, team.playerGroups, state.statuses, cells),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [team.players, dateStrs, state.filters, team.playerGroups, state.statuses, state.byMonth]
    );

    // Reset to page 1 whenever filters/month change (never shows an empty slice).
    useEffect(() => setPage(1), [state.filters]);

    const pageRows = useMemo(
        () => fullRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [fullRows, page]
    );

    // Header status counts over the FILTERED rows (full set, all pages) — matches v1. Computed here
    // (this component re-renders on filter change) and fed into `columns` so antd rebuilds the header.
    const headerCounts = useMemo(() => {
        const ids = new Set(fullRows.map((r) => r.player?.player_id));
        return computeStatusCounts(filterCellsByPlayerIds(cells, ids), state.statuses);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullRows, state.byMonth, state.statuses]);

    const dateColumns = useMemo(
        () =>
            dateStrs.map((dt) => ({
                title: columnHeader(dt),
                dataIndex: dt,
                key: dt,
                width: 70,
                render: (uniqueKey) => (uniqueKey ? <DateCell uniqueKey={uniqueKey} /> : null),
            })),
        [dateStrs]
    );

    const columns = useMemo(
        () => [
            {
                title: <PlayerColumnHeader counts={headerCounts} />,
                dataIndex: "player",
                key: "player",
                fixed: "left",
                width: 320,
                render: (player) => <PlayerColumnCell player={player} />,
            },
            ...dateColumns,
        ],
        [dateColumns, headerCounts]
    );

    return (
        <>
            <div className="stv2-tracker-legend-pagination-row">
                <StatusLegend
                    statuses={state.statuses}
                    leading={legendLeading}
                    trailing={<AddFlagButton tooltipPlacement={flagTooltipPlacement} />}
                />
                <Pagination
                    current={page}
                    pageSize={PAGE_SIZE}
                    total={Math.max(fullRows.length, 1)}
                    onChange={setPage}
                    hideOnSinglePage={false}
                    showSizeChanger={false}
                />
            </div>
            <div className="stv2-tracker-table-container">
                <Table
                    className="stv2-status-tracker-table"
                    columns={columns}
                    dataSource={pageRows}
                    bordered
                    pagination={false}
                    scroll={{ y: 550 }}
                    sticky
                    rowKey="id"
                />
            </div>
        </>
    );
};
